import { useEffect, useMemo, useState } from 'react';
import {
  FileCheck, Eye, Edit3, Send, X, AlertCircle, Check, RefreshCw, Filter,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_LABELS = {
  letter:             'Lettre du cabinet',
  context:            'I — Contexte officinal',
  global_position:    'II — Position globale',
  alerts:             'III — Signaux clés',
  family_activite:    'IV — Activité commerciale',
  family_rentabilite: 'IV — Rentabilité',
  family_stock:       'IV — Stock & trésorerie',
  family_patientele:  'IV — Patientèle & services',
  action_plan:        "V — Plan d'action",
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const fmtPeriod = (period) => {
  if (!period) return '—';
  const months = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
  const [y, m] = period.split('-');
  return `${months[parseInt(m, 10) - 1] || period} ${y}`;
};

function authHeaders() {
  const token = localStorage.getItem('benchory_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function SectionEditModal({ reportId, section, onClose, onSaved }) {
  const [contentMd, setContentMd] = useState(section?.content_md || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!section) return null;

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const res = await fetch(
        `${API_URL}/admin/monthly-reports/${reportId}/section/${section.kind}`,
        {
          method: 'PATCH',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ content_md: contentMd }),
        },
      );
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Échec de la sauvegarde');
      }
      onSaved?.();
      onClose();
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[1100]" data-testid="section-edit-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-3xl bg-white shadow-2xl flex flex-col">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">Édition manuelle</div>
            <h2 className="font-bold text-[#1B2A4A] font-manrope">{SECTION_LABELS[section.kind] || section.kind}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="section-edit-close">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {error && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-center gap-2">
              <AlertCircle size={13} /> {error}
            </div>
          )}
          {section.error && (
            <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
              <strong>Erreur de génération :</strong> {section.error}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Contenu rédigé (markdown)
            </label>
            <textarea
              value={contentMd}
              onChange={(e) => setContentMd(e.target.value)}
              rows={20}
              data-testid="section-edit-textarea"
              className="w-full font-mono text-[12px] text-[#1B2A4A] bg-[#F8F9FA] border border-gray-200 rounded-lg p-3 leading-relaxed focus:outline-none focus:border-[#1B2A4A] resize-y"
              spellCheck={false}
            />
          </div>
          {section.content_data && Object.keys(section.content_data).length > 0 && (
            <details className="bg-gray-50 rounded-lg p-3" data-testid="section-content-data">
              <summary className="text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                Données structurées (JSON, lecture seule)
              </summary>
              <pre className="text-[10px] text-gray-600 mt-2 overflow-x-auto">{JSON.stringify(section.content_data, null, 2)}</pre>
            </details>
          )}
          {section.edited_at && (
            <p className="text-[10px] text-gray-400">Dernière édition manuelle : {fmtDate(section.edited_at)} par {section.edited_by || '—'}</p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3 bg-[#F8F9FA]">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 font-medium hover:text-[#1B2A4A]">
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="section-edit-save"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-[#1B2A4A] text-white hover:bg-[#152039] disabled:opacity-50"
          >
            <Check size={14} /> {saving ? 'Sauvegarde…' : 'Enregistrer la section'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReportDetailPanel({ report, onClose, onPublished, onSectionUpdated }) {
  const [editingSection, setEditingSection] = useState(null);
  const [publishing, setPublishing] = useState(false);

  const handlePublish = async () => {
    if (!window.confirm(`Publier le rapport ${report.period} ?`)) return;
    setPublishing(true);
    try {
      const res = await fetch(`${API_URL}/admin/monthly-reports/${report.id}/publish`, {
        method: 'PATCH', headers: authHeaders(),
      });
      if (res.ok) onPublished?.(report.id);
    } catch { /* silent */ }
    setPublishing(false);
  };

  const handleUnpublish = async () => {
    if (!window.confirm('Repasser ce rapport en brouillon ?')) return;
    try {
      const res = await fetch(`${API_URL}/admin/monthly-reports/${report.id}/unpublish`, {
        method: 'PATCH', headers: authHeaders(),
      });
      if (res.ok) onPublished?.(report.id);
    } catch { /* silent */ }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm" data-testid="report-detail-panel">
      <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">Rapport cabinet</div>
          <h2 className="font-bold text-[#1B2A4A] font-manrope text-lg">
            {fmtPeriod(report.period)} · {report.establishment_name}
          </h2>
          <div className="text-xs text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
            <span>Indice <strong className="text-[#1B2A4A]">{report.indice_benchory ?? '—'}</strong></span>
            <span>·</span>
            <span>{(report.satellite_signals_selected || []).length} signaux</span>
            <span>·</span>
            <span>{(report.errors || []).length} erreur(s) section</span>
            <span>·</span>
            <span>Code {report.code_officine || '—'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {report.published ? (
            <button
              type="button"
              onClick={handleUnpublish}
              data-testid="unpublish-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              Repasser en brouillon
            </button>
          ) : (
            <button
              type="button"
              onClick={handlePublish}
              disabled={publishing}
              data-testid="publish-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-[#1B2A4A] text-white hover:bg-[#152039] disabled:opacity-50"
            >
              <Send size={13} /> {publishing ? 'Publication…' : 'Publier'}
            </button>
          )}
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="detail-close">
            <X size={16} className="text-gray-500" />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-3">
        {(report.cabinet_sections || []).map((section) => (
          <div
            key={section.kind}
            className={`border rounded-lg p-4 ${section.error ? 'border-amber-200 bg-amber-50/40' : 'border-gray-100'}`}
            data-testid={`section-row-${section.kind}`}
          >
            <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
              <div>
                <h3 className="font-semibold text-sm text-[#1B2A4A]">{SECTION_LABELS[section.kind] || section.kind}</h3>
                <div className="text-[10px] text-gray-400 mt-0.5">
                  Généré le {fmtDate(section.generated_at)}
                  {section.prompt_version_used && ` · prompt V${section.prompt_version_used}`}
                  {section.edited_at && ` · édité le ${fmtDate(section.edited_at)}`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditingSection(section)}
                data-testid={`edit-section-${section.kind}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded-lg border border-gray-200 text-gray-600 hover:border-[#1B2A4A] hover:text-[#1B2A4A]"
              >
                <Edit3 size={12} /> Éditer
              </button>
            </div>
            {section.error && (
              <div className="text-[11px] text-amber-700 bg-amber-100 border border-amber-200 rounded px-2 py-1 mb-2">
                Erreur de génération : {section.error}
              </div>
            )}
            {section.warning && (
              <div className="text-[11px] text-gray-500 italic mb-2">⚑ {section.warning}</div>
            )}
            <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">
              {section.content_md ? section.content_md.slice(0, 280) + (section.content_md.length > 280 ? '…' : '') : <em className="text-gray-400">Aucun contenu rédigé.</em>}
            </p>
          </div>
        ))}
      </div>

      {editingSection && (
        <SectionEditModal
          reportId={report.id}
          section={editingSection}
          onClose={() => setEditingSection(null)}
          onSaved={() => onSectionUpdated?.()}
        />
      )}
    </div>
  );
}

export default function ReportReviewPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('draft');
  const [periodFilter, setPeriodFilter] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.set('status', statusFilter);
    fetch(`${API_URL}/admin/monthly-reports?${params}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => {
        setReports(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    load();
    setSelectedReport(null);
    setSelectedIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const filtered = useMemo(() => {
    return reports.filter((r) => !periodFilter || r.period === periodFilter);
  }, [reports, periodFilter]);

  const allPeriods = useMemo(() => {
    const set = new Set(reports.map((r) => r.period).filter(Boolean));
    return [...set].sort().reverse();
  }, [reports]);

  const loadSelectedDetail = async (reportId) => {
    try {
      const res = await fetch(`${API_URL}/admin/monthly-reports/${reportId}`, { headers: authHeaders() });
      if (res.ok) setSelectedReport(await res.json());
    } catch { /* silent */ }
  };

  const refreshSelectedAndList = async () => {
    if (selectedReport?.id) await loadSelectedDetail(selectedReport.id);
    load();
  };

  const toggleSelected = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const handleBulkPublish = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Publier ${selectedIds.size} rapport(s) ?`)) return;
    setBulkPublishing(true);
    setBulkResult(null);
    try {
      const res = await fetch(`${API_URL}/admin/monthly-reports/publish-batch`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_ids: [...selectedIds] }),
      });
      if (res.ok) {
        const result = await res.json();
        setBulkResult(result);
        setSelectedIds(new Set());
        load();
      }
    } catch { /* silent */ }
    setBulkPublishing(false);
  };

  return (
    <div className="space-y-6" data-testid="report-review-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1B2A4A] font-manrope tracking-tight">Revue des rapports cabinet</h1>
          <p className="text-gray-400 mt-1 text-sm">
            Lecture rapide des rapports en brouillon, édition manuelle des sections, publication par lot.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="reload-btn"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:border-[#1B2A4A]"
        >
          <RefreshCw size={13} /> Actualiser
        </button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {[
            { k: 'draft', l: 'Brouillons' },
            { k: 'published', l: 'Publiés' },
            { k: 'all', l: 'Tous' },
          ].map((s) => (
            <button
              key={s.k}
              onClick={() => setStatusFilter(s.k)}
              data-testid={`status-filter-${s.k}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                statusFilter === s.k ? 'bg-white text-[#1B2A4A] shadow-sm' : 'text-gray-500'
              }`}
            >
              {s.l}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Filter size={13} className="text-gray-400" />
          <select
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
            data-testid="period-filter"
            className="h-8 px-3 rounded-lg border border-gray-200 text-xs bg-white"
          >
            <option value="">Toutes périodes</option>
            {allPeriods.map((p) => (
              <option key={p} value={p}>{fmtPeriod(p)}</option>
            ))}
          </select>
        </div>

        {selectedIds.size > 0 && statusFilter === 'draft' && (
          <button
            type="button"
            onClick={handleBulkPublish}
            disabled={bulkPublishing}
            data-testid="bulk-publish-btn"
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#1B2A4A] text-white hover:bg-[#152039] disabled:opacity-50"
          >
            <Send size={13} /> {bulkPublishing ? 'Publication…' : `Publier ${selectedIds.size} rapport(s)`}
          </button>
        )}
      </div>

      {bulkResult && (
        <div className="px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700 flex items-center gap-2">
          <Check size={14} /> {bulkResult.published} rapport(s) publié(s) · {bulkResult.skipped} ignoré(s)
        </div>
      )}

      <div className={`grid gap-6 ${selectedReport ? 'grid-cols-1 lg:grid-cols-[1fr_1.4fr]' : 'grid-cols-1'}`}>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full" data-testid="reports-table">
            <thead className="bg-[#F8F9FA] border-b border-gray-100">
              <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                {statusFilter === 'draft' && <th className="px-3 py-3 w-10"></th>}
                <th className="px-3 py-3">Période</th>
                <th className="px-3 py-3">Officine</th>
                <th className="px-3 py-3 text-right">Indice</th>
                <th className="px-3 py-3 text-center">Erreurs</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center text-sm text-gray-400 py-12">Chargement…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="6" className="text-center text-sm text-gray-400 py-12">Aucun rapport pour ce filtre.</td></tr>
              ) : filtered.map((report) => {
                const errCount = (report.errors || []).length;
                return (
                  <tr key={report.id} className={`border-b border-gray-50 hover:bg-[#F8F9FA] transition ${selectedReport?.id === report.id ? 'bg-[#1B2A4A]/[0.03]' : ''}`} data-testid={`report-row-${report.id}`}>
                    {statusFilter === 'draft' && (
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(report.id)}
                          onChange={() => toggleSelected(report.id)}
                          data-testid={`select-${report.id}`}
                          className="cursor-pointer accent-[#1B2A4A]"
                        />
                      </td>
                    )}
                    <td className="px-3 py-3">
                      <div className="text-sm font-semibold text-[#1B2A4A]">{fmtPeriod(report.period)}</div>
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        {report.published ? `Publié ${fmtDate(report.published_at)}` : `Généré ${fmtDate(report.generated_at)}`}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-sm text-[#1B2A4A]">{report.establishment_name || '—'}</div>
                      <div className="text-[10px] text-gray-400">{report.code_officine || '—'}</div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <span className="font-bold text-[#1B2A4A]">{report.indice_benchory ?? '—'}</span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      {errCount > 0 ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                          <AlertCircle size={11} /> {errCount}
                        </span>
                      ) : (
                        <span className="text-[11px] text-emerald-600">✓</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => loadSelectedDetail(report.id)}
                        data-testid={`view-${report.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium text-gray-600 border border-gray-200 hover:border-[#1B2A4A] hover:text-[#1B2A4A]"
                      >
                        <Eye size={12} /> Voir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {selectedReport && (
          <ReportDetailPanel
            report={selectedReport}
            onClose={() => setSelectedReport(null)}
            onPublished={refreshSelectedAndList}
            onSectionUpdated={refreshSelectedAndList}
          />
        )}
      </div>
    </div>
  );
}
